import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { FileText, ShieldCheck, CheckCircle2, MessageSquareWarning, ExternalLink, Download } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("de-CH", { dateStyle: "medium" }); } catch { return iso; } };
const fmtSize = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

/** Öffentliche Seite für den Treuhänder: Steuerunterlagen ansehen und als geprüft bestätigen (Token-Link, kein Login). */
export default function TaxSharePage() {
  const { token } = useParams();
  const [share, setShare] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/tax-share/${token}`).then((r) => setShare(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Link ungültig."));
  }, [token]);

  const review = async (decision) => {
    if (!name.trim()) { toast.error("Bitte deinen Namen angeben"); return; }
    if (decision === "changes_requested" && !comment.trim()) { toast.error("Bitte die Rückfrage kurz beschreiben"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/public/tax-share/${token}/review`, { decision, reviewer_name: name, comment });
      setShare((s) => ({ ...s, status: decision, reviewer_name: name, comment, reviewed_at: new Date().toISOString() }));
      toast.success(decision === "approved" ? "Danke – die Unterlagen sind als geprüft bestätigt." : "Rückfrage übermittelt.");
    } catch (e) { toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  const fileUrl = (idx, inline = true) => `${API}/public/tax-share/${token}/file/${idx}?inline=${inline}`;

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="tax-share-page">
      <div className="mx-auto max-w-3xl space-y-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-slate-900 text-white flex items-center justify-center"><ShieldCheck className="h-5 w-5" /></div>
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Steuerunterlagen zur Prüfung</h1>
            <p className="text-sm text-slate-500">Sicherer Link · kein Login nötig</p>
          </div>
        </div>
        {error && <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" data-testid="tax-share-error">{error}</div>}
        {share && (
          <>
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-lg font-semibold text-slate-900" data-testid="tax-share-title">{share.company_name} · Steuerjahr {share.year}</div>
              <div className="text-xs text-slate-500 mt-1">Bereitgestellt am {fmtDate(share.created_at)} · gültig bis {fmtDate(share.expires_at)}</div>
              {share.message && <p className="mt-3 text-sm text-slate-700 whitespace-pre-line" data-testid="tax-share-message">{share.message}</p>}
              <ul className="mt-4 divide-y divide-slate-100 rounded-md border border-slate-100" data-testid="tax-share-files">
                {share.files.map((f) => (
                  <li key={f.idx} className="flex items-center gap-3 px-3 py-2.5" data-testid={`tax-share-file-${f.idx}`}>
                    <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{f.name}</div>
                      <div className="text-xs text-slate-500">{fmtSize(f.size)}</div>
                    </div>
                    <a href={fileUrl(f.idx)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-sky-700 hover:underline" data-testid={`tax-share-open-${f.idx}`}><ExternalLink className="h-3.5 w-3.5" />Ansehen</a>
                    <a href={fileUrl(f.idx, false)} className="inline-flex items-center gap-1 text-xs text-slate-600 hover:underline" data-testid={`tax-share-download-${f.idx}`}><Download className="h-3.5 w-3.5" />Download</a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="tax-share-review-box">
              {share.status === "approved" ? (
                <div className="flex items-start gap-3 text-emerald-800" data-testid="tax-share-approved">
                  <CheckCircle2 className="h-5 w-5 mt-0.5" />
                  <div><div className="font-semibold">Als geprüft bestätigt</div><div className="text-sm">von {share.reviewer_name} am {fmtDate(share.reviewed_at)}{share.comment ? ` · «${share.comment}»` : ""}</div></div>
                </div>
              ) : (
                <>
                  {share.status === "changes_requested" && (
                    <div className="mb-3 flex items-start gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-900" data-testid="tax-share-changes">
                      <MessageSquareWarning className="h-4 w-4 mt-0.5" />Rückfrage gemeldet von {share.reviewer_name} am {fmtDate(share.reviewed_at)}: «{share.comment}» – du kannst nach Klärung weiterhin bestätigen.
                    </div>
                  )}
                  <div className="font-semibold text-slate-900 mb-2">Prüfung abschliessen</div>
                  <div className="grid gap-2 md:grid-cols-2">
                    <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Dein Name (Treuhänder/in)" className="rounded-md border border-slate-300 px-3 py-2 text-sm" data-testid="tax-share-reviewer-name" />
                    <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Kommentar / Rückfrage (optional)" className="rounded-md border border-slate-300 px-3 py-2 text-sm" data-testid="tax-share-comment" />
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button type="button" onClick={() => review("approved")} disabled={busy} className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50" data-testid="tax-share-approve-button"><CheckCircle2 className="h-4 w-4" />Als geprüft bestätigen</button>
                    <button type="button" onClick={() => review("changes_requested")} disabled={busy} className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50" data-testid="tax-share-changes-button"><MessageSquareWarning className="h-4 w-4" />Rückfrage senden</button>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
